import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, Send, LogOut, Loader2 } from "lucide-react";

export default function CheckInbox() {
  const { user, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  const resend = async () => {
    setResending(true);
    try {
      const { data } = await http.post("/auth/resend-verification");
      if (data?.already_verified) {
        toast.success("Already verified — taking you to the dashboard.");
        navigate("/dashboard");
      } else {
        toast.success(`Verification email sent to ${data?.sent_to || user?.email}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to resend";
      if (e?.response?.status === 429) {
        toast.info("Please wait a moment before requesting another email.");
      } else {
        toast.error(msg);
      }
    } finally {
      setResending(false);
    }
  };

  const onLogout = async () => {
    try { await logout(); } catch (_e) { /* non-blocking */ }
    navigate("/login");
  };

  return (
    <main className="min-h-screen grid place-items-center bg-ink-50 px-4 py-16">
      <div
        data-testid="check-inbox-card"
        className="bg-white border border-ink-200 rounded-sm p-8 max-w-md w-full shadow-sm"
      >
        {/* Loud banner so the verification requirement is impossible to miss. */}
        <div
          data-testid="verify-banner"
          className="mb-6 rounded-sm border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-left"
        >
          <div className="text-[10px] uppercase tracking-widestx font-semibold text-amber-700 mb-1">
            Action required · Verify your email
          </div>
          <p className="text-xs text-amber-900 leading-relaxed">
            Your account is created but <strong>not yet active</strong>. Open your email
            inbox, find the message from LeadIQ Pro, and click the verification link to
            unlock the dashboard.
          </p>
        </div>

        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-brand/10 grid place-items-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-brand" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-ink-900 tracking-tight mb-2">
            Check your inbox
          </h1>
          <p className="text-sm text-ink-600 mb-1">We sent a verification link to</p>
          <p
            data-testid="check-inbox-email"
            className="font-mono text-sm text-ink-900 mb-6 break-all"
          >
            {user?.email || "your email"}
          </p>

          <p className="text-xs text-ink-500 mb-5 leading-relaxed">
            Didn&apos;t get it? Check your <strong>Spam</strong> or{" "}
            <strong>Promotions</strong> folder. If it&apos;s still missing, tap below to
            send a fresh verification link.
          </p>

          <Button
            data-testid="resend-verification-btn"
            onClick={resend}
            disabled={resending}
            className="w-full bg-brand hover:bg-brand-hover text-white rounded-sm h-11 text-[11px] uppercase tracking-widestx mb-2 font-semibold"
          >
            {resending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Resend verification link
          </Button>
          <Button
            data-testid="check-inbox-logout"
            variant="outline"
            onClick={onLogout}
            className="w-full rounded-sm h-10 text-[11px] uppercase tracking-widestx"
          >
            <LogOut className="w-3.5 h-3.5" />
            Wrong email · log out
          </Button>
        </div>
      </div>
    </main>
  );
}
